import {
  ALL_SALARY_CODES_QUERY,
  type SalaryCodeNode,
} from '@/modules/companies'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'
import { useNodes } from '@/utils/hooks/useNodes'

interface CommonProps extends ComboboxCommonProps<string> {
  alwaysInclude?: Omit<SalaryCodeNode, '__typename'> | null
  exclude?: string[]
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  clearable?: boolean
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  clearable?: false
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function SalaryCodeCombobox({
  mode,
  value,

  alwaysInclude = null,
  exclude = [],
  clearable = false,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.salary-code.placeholder',
      search: 'common.search',
      noResults: 'common.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.salary-code.placeholder',
      search: 'common.search',
      noResults: 'common.no-results',
    },
  })

  const { data: salaryCodesData } = useQuery(ALL_SALARY_CODES_QUERY)

  const salaryCodes = useNodes(salaryCodesData?.allSalaryCodes)

  const salaryCodeItems: ComboboxItem[] = (() => {
    const items = salaryCodes
      .filter(
        salaryCode =>
          salaryCode.id !== alwaysInclude?.id &&
          !exclude.includes(salaryCode.id)
      )
      .map<ComboboxItem>(user => ({
        value: user.id,
        label: user.name,
      }))

    if (!!alwaysInclude)
      items.unshift({
        value: alwaysInclude.id,
        label: alwaysInclude.name,
      })

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={salaryCodeItems}
        clearable={clearable}
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={searchPlaceholder ?? translations.single.search}
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={salaryCodeItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={searchPlaceholder ?? translations.multiple.search}
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
